import { Collapse, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import { FundAccountType } from 'model/enums/fund-account-type';
import { HeaderVisualizationType } from 'model/enums/header-visualization-type';
import { YearlyFinancialStatementMonth } from 'model/financial-statement-month';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetYearlyFinancialStatementMonthState } from 'reducer/hooks';
import { useFundAndSeriesContext } from 'shared/fund-and-series-selection/fund-and-series-selection-context';
import { v4 as uuidv4 } from 'uuid';
import ExpandableFirstCell from '../expandable-first-cell';
import YearlyFinancialStatementMonthRow from '../yearly-financial-statement-month-row';
import { fillArrayWithNullValues } from './utils/array-utils';

const I18N_PREFIX = 'anchor.financial-statement-month';

type ExpandedAccountsTypes = {
    active: boolean;
    passive: boolean;
    debentures: boolean;
};

const defaultExpandedAccounts: ExpandedAccountsTypes = {
    active: true,
    passive: true,
    debentures: false
};

const YearlyFinancialStatementMonthTable = () => {
    const { t } = useTranslation();

    const { yearlyFinancialStatementMonth } = useGetYearlyFinancialStatementMonthState();
    const { selectedFundAndSeries } = useFundAndSeriesContext();

    const [expandedAccounts, setExpandedAccounts] = React.useState<ExpandedAccountsTypes>(defaultExpandedAccounts);

    const toggleExpandedAccounts = (account: keyof ExpandedAccountsTypes) => () => {
        setExpandedAccounts(prev => ({ ...prev, [account]: !prev[account] }));
    };

    if (!yearlyFinancialStatementMonth) {
        return <></>;
    }

    const yearlyStatement: YearlyFinancialStatementMonth = {
        years: fillArrayWithNullValues(yearlyFinancialStatementMonth.years),
        balances: fillArrayWithNullValues(yearlyFinancialStatementMonth.balances),
        accounts: fillArrayWithNullValues(yearlyFinancialStatementMonth.accounts),
        debentures: {
            balances: fillArrayWithNullValues(yearlyFinancialStatementMonth.debentures.balances),
            series: yearlyFinancialStatementMonth.debentures.series
        }
    };

    const isSecondaryAccessActive: boolean = selectedFundAndSeries?.headerVisualizationType === HeaderVisualizationType.SECONDARY_ACCESS;

    return (
        <>
            {yearlyFinancialStatementMonth?.years?.length > 0 ? (
                <table
                    className={clsx('page-container--table', {
                        SECONDARY_ACCESS_ACTIVE: isSecondaryAccessActive
                    })}
                >
                    <thead className="page-container--table__header">
                        <tr>
                            <th className="page-container--table__sticky first-columns none-border" />
                            {yearlyStatement?.years.map(year => {
                                return (
                                    <th key={year ?? uuidv4()} className="page-container--table__header-items">
                                        {year ?? '-'}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="active" onClick={toggleExpandedAccounts('active')}>
                            <ExpandableFirstCell className="active" cellTitle={t(`${I18N_PREFIX}.active`)} isCollapsedArrow={expandedAccounts.active} />
                            <YearlyFinancialStatementMonthRow years={yearlyStatement?.years} balances={yearlyStatement?.balances?.map(it => ({ id: it?.id, year: it?.year, value: it?.totalActive })) ?? []} className="active" />
                        </tr>

                        {yearlyStatement?.accounts
                            ?.filter(fundAccount => fundAccount?.type === FundAccountType.ACTIVE)
                            ?.map(fundAccount => (
                                <Collapse in={expandedAccounts.active} key={fundAccount?.id}>
                                    <tr>
                                        <td className="page-container--table__sticky first-columns">
                                            <Tooltip title={fundAccount?.name}>
                                                <span>{fundAccount?.name}</span>
                                            </Tooltip>
                                        </td>

                                        <YearlyFinancialStatementMonthRow years={yearlyStatement?.years} balances={fundAccount?.balances} />
                                    </tr>
                                </Collapse>
                            ))}

                        <tr className="passive" onClick={toggleExpandedAccounts('passive')}>
                            <ExpandableFirstCell className="passive" cellTitle={t(`${I18N_PREFIX}.passive`)} isCollapsedArrow={expandedAccounts.passive} />
                            <YearlyFinancialStatementMonthRow years={yearlyStatement?.years} balances={yearlyStatement?.balances?.map(it => ({ id: it?.id, year: it?.year, value: it?.totalPassive })) ?? []} className="passive" />
                        </tr>
                        {yearlyStatement?.accounts
                            ?.filter(fundAccount => fundAccount?.type === FundAccountType.PASSIVE)
                            .map(fundAccount => (
                                <Collapse in={expandedAccounts.passive} key={fundAccount?.id}>
                                    <tr>
                                        <td className="page-container--table__sticky first-columns">
                                            <Tooltip title={fundAccount?.name}>
                                                <span>{fundAccount?.name}</span>
                                            </Tooltip>
                                        </td>

                                        <YearlyFinancialStatementMonthRow years={yearlyStatement?.years} balances={fundAccount?.balances} className="passive-color" />
                                    </tr>
                                </Collapse>
                            ))}
                        <Collapse in={expandedAccounts.passive}>
                            <tr className="pointer" onClick={toggleExpandedAccounts('debentures')}>
                                <ExpandableFirstCell className="debentures-columns" cellTitle={t(`${I18N_PREFIX}.debentures`)} isCollapsedArrow={expandedAccounts.debentures} />
                                <YearlyFinancialStatementMonthRow years={yearlyStatement?.years} balances={yearlyStatement?.debentures?.balances ?? []} className="passive-weighted-color" />
                            </tr>
                        </Collapse>
                        {yearlyStatement?.debentures?.series?.map(series => (
                            <Collapse in={expandedAccounts.passive && expandedAccounts.debentures} key={series?.id ?? uuidv4()}>
                                <tr>
                                    <td className="page-container--table__sticky debentures-series">
                                        <Tooltip title={series?.name}>
                                            <span>{series?.name}</span>
                                        </Tooltip>
                                    </td>
                                    {<YearlyFinancialStatementMonthRow years={yearlyStatement?.years} balances={series?.balances} className="passive-debentures-color" />}
                                </tr>
                            </Collapse>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="financial-statement-month__table--empty-years">{t(`${I18N_PREFIX}.empty-yearly-statement`)}</div>
            )}
        </>
    );
};

export default YearlyFinancialStatementMonthTable;
